import {
  ConstructionStatus,
  DAY_SHORT,
  type ProjectAdminDto,
  FINANCING_LABELS,
  FinancingType,
  PROJECT_PROGRAM_LABELS,
  PROJECT_PROGRAMS,
  type ProjectProgram,
} from '@performance-crm/shared';
import { Paperclip, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AttachmentsDialog } from '@/components/attachments/AttachmentsDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast-context';
import { fmt, toDateInput, describeProjectCommercial } from '@/lib/format';
import {
  useAddSubdivision,
  useAdminProjects,
  useCreateProject,
  useDeleteSubdivision,
  useUpdateProject,
  useBotKnowledge,
} from '@/lib/queries';

const STATUS_LABEL: Record<ConstructionStatus, string> = {
  PREVENTA: 'Preventa',
  EN_CONSTRUCCION: 'En construcción',
  ENTREGA_INMEDIATA: 'Entrega inmediata',
  ENTREGADO: 'Entregado',
};

export function ProjectsTab({ companyId }: { companyId: string }) {
  const { data: projects, isLoading } = useAdminProjects(companyId);
  const update = useUpdateProject();
  const addSub = useAddSubdivision();
  const delSub = useDeleteSubdivision();
  const toast = useToast();
  const [editing, setEditing] = useState<ProjectAdminDto | 'new' | null>(null);
  const [subFor, setSubFor] = useState<ProjectAdminDto | null>(null);
  const [files, setFiles] = useState<ProjectAdminDto | null>(null);
  if (isLoading) return <Skeleton className="h-64 w-full" />;
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setEditing('new')} data-testid="btn-new-project">
          <Plus className="h-4 w-4" /> Nuevo proyecto
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {projects?.map((p) => (
          <Card key={p.id} data-testid="project-card" className={!p.active ? 'opacity-60' : ''}>
            <CardContent className="space-y-2 p-4 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold">
                    {p.name} {!p.active && <Badge tone="muted">inactivo</Badge>}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {p.district ?? '—'}
                    {p.constructionStatus ? ` · ${STATUS_LABEL[p.constructionStatus]}` : ''}
                    {p.deliveryDate ? ` · entrega ${fmt.date(p.deliveryDate)}` : ''}
                  </p>
                  {describeProjectCommercial(p.commercial) && (
                    <p className="text-xs" data-testid="project-commercial">
                      {describeProjectCommercial(p.commercial)}
                    </p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Archivos del proyecto"
                    onClick={() => setFiles(p)}
                    data-testid="btn-project-files"
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Editar proyecto"
                    onClick={() => setEditing(p)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      update
                        .mutateAsync({ id: p.id, input: { active: !p.active } })
                        .then(() =>
                          toast.success(p.active ? 'Proyecto desactivado' : 'Proyecto activado'),
                        )
                        .catch((e: Error) => toast.error('No se pudo actualizar', e.message))
                    }
                  >
                    {p.active ? 'Desactivar' : 'Activar'}
                  </Button>
                </div>
              </div>
              <p className="text-muted-foreground text-xs">
                {p.units} unidades · {p.leads} leads ·{' '}
                <Link
                  to={`/comercial?tab=stock&projectId=${p.id}`}
                  className="text-primary underline"
                >
                  ver stock
                </Link>
              </p>
              <div className="flex flex-wrap items-center gap-1">
                {p.subdivisions.map((s) => (
                  <Badge key={s.id} tone="outline">
                    {s.name} ({s.units})
                    {s.units === 0 && (
                      <button
                        type="button"
                        aria-label={`Borrar ${s.name}`}
                        onClick={() =>
                          delSub
                            .mutateAsync({ projectId: p.id, id: s.id })
                            .catch((e: Error) => toast.error('No se pudo borrar', e.message))
                        }
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </Badge>
                ))}
                <button
                  type="button"
                  className="text-primary text-xs underline"
                  onClick={() => setSubFor(p)}
                >
                  + torre / etapa
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {files && (
        <AttachmentsDialog
          target={{ entityType: 'PROJECT', entityId: files.id, label: files.name }}
          canUpload
          onClose={() => setFiles(null)}
        />
      )}
      {editing && (
        <ProjectDialog
          companyId={companyId}
          project={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {subFor && (
        <SubdivisionDialog
          onClose={() => setSubFor(null)}
          onSave={async (input) => {
            try {
              await addSub.mutateAsync({ projectId: subFor.id, input });
              toast.success('Subdivisión creada');
              setSubFor(null);
            } catch (err) {
              toast.error('No se pudo crear', (err as Error).message);
            }
          }}
        />
      )}
    </div>
  );
}

function ProjectDialog({
  companyId,
  project,
  onClose,
}: {
  companyId: string;
  project: ProjectAdminDto | null;
  onClose: () => void;
}) {
  const create = useCreateProject();
  const update = useUpdateProject();
  const toast = useToast();
  const [f, setF] = useState({
    name: project?.name ?? '',
    district: project?.district ?? '',
    address: project?.address ?? '',
    constructionStatus: project?.constructionStatus ?? '',
    deliveryDate: toDateInput(project?.deliveryDate),
    active: project?.active ?? true,
    // Ficha comercial
    reservationAmount: project?.commercial.reservationAmount?.toString() ?? '',
    reservationCurrency: project?.commercial.reservationCurrency ?? 'USD',
    initialFeePercent: project?.commercial.initialFeePercent?.toString() ?? '',
    initialInstallments: project?.commercial.initialInstallments?.toString() ?? '',
    financingTypes: project?.commercial.financingTypes ?? ([] as FinancingType[]),
    banks: project?.commercial.banks.join(', ') ?? '',
    programs: project?.commercial.programs ?? ([] as ProjectProgram[]),
    promotions: project?.commercial.promotions ?? '',
    notes: project?.commercial.notes ?? '',
    // Horario de la sala de ventas (null = el de la empresa)
    roomCustom: !!project?.salesRoomHours,
    roomStart: project?.salesRoomHours?.start ?? '09:00',
    roomEnd: project?.salesRoomHours?.end ?? '18:00',
    roomDays: project?.salesRoomHours?.days ?? [1, 2, 3, 4, 5, 6],
    // Lo que sabe el bot
    botInstructions: project?.bot.instructions ?? '',
    botFaqs: (project?.bot.faqs ?? []) as Array<{ q: string; a: string }>,
    botPriceMode: (project?.bot.priceMode ?? '') as '' | 'DESDE' | 'NO',
    botPrices: (project?.bot.pricesFrom ?? []).map((x) => ({
      typology: x.typology,
      from: String(x.from),
      currency: x.currency,
    })),
  });
  const [preview, setPreview] = useState(false);
  const set = (patch: Partial<typeof f>) => setF((p) => ({ ...p, ...patch }));
  const toggleIn = <T extends string>(list: T[], value: T) =>
    list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const input = {
      name: f.name,
      district: f.district || null,
      address: f.address || null,
      constructionStatus: (f.constructionStatus || null) as ConstructionStatus | null,
      deliveryDate: f.deliveryDate ? new Date(`${f.deliveryDate}T12:00:00`) : null,
      active: f.active,
      commercial: {
        reservationAmount: f.reservationAmount ? Number(f.reservationAmount) : null,
        reservationCurrency: f.reservationCurrency as 'USD' | 'PEN',
        initialFeePercent: f.initialFeePercent ? Number(f.initialFeePercent) : null,
        initialInstallments: f.initialInstallments ? Number(f.initialInstallments) : null,
        financingTypes: f.financingTypes,
        banks: f.banks
          .split(',')
          .map((b) => b.trim())
          .filter(Boolean),
        programs: f.programs,
        promotions: f.promotions || null,
        notes: f.notes || null,
      },
      salesRoomHours: f.roomCustom
        ? { start: f.roomStart, end: f.roomEnd, days: f.roomDays }
        : null,
      bot: {
        instructions: f.botInstructions || null,
        faqs: f.botFaqs.filter((x) => x.q.trim() && x.a.trim()),
        priceMode: f.botPriceMode || null,
        pricesFrom: f.botPrices
          .filter((x) => x.typology.trim() && Number(x.from) > 0)
          .map((x) => ({
            typology: x.typology.trim(),
            from: Number(x.from),
            currency: x.currency,
          })),
      },
    };
    try {
      if (project) await update.mutateAsync({ id: project.id, input });
      else await create.mutateAsync({ companyId, ...input });
      toast.success(project ? 'Proyecto actualizado' : 'Proyecto creado');
      onClose();
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{project ? `Editar ${project.name}` : 'Nuevo proyecto'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="p-name">Nombre</Label>
              <Input
                id="p-name"
                value={f.name}
                onChange={(e) => set({ name: e.target.value })}
                required
                minLength={2}
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="p-district">Distrito</Label>
              <Input
                id="p-district"
                value={f.district}
                onChange={(e) => set({ district: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="p-address">Dirección</Label>
              <Input
                id="p-address"
                value={f.address}
                onChange={(e) => set({ address: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="p-status">Estado de obra</Label>
              <Select
                id="p-status"
                value={f.constructionStatus}
                onChange={(e) => set({ constructionStatus: e.target.value })}
              >
                <option value="">—</option>
                {Object.values(ConstructionStatus).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="p-delivery">Fecha de entrega</Label>
              <Input
                id="p-delivery"
                type="date"
                value={f.deliveryDate}
                onChange={(e) => set({ deliveryDate: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={f.active}
                onChange={(e) => set({ active: e.target.checked })}
                className="h-4 w-4"
              />
              Activo (visible para captar leads)
            </label>
          </div>
          <div className="space-y-3 border-t pt-3">
            <div>
              <p className="text-sm font-medium">Ficha comercial</p>
              <p className="text-muted-foreground text-xs">
                Lo que el asesor y el bot necesitan para cotizar: se prellena en separaciones y
                proformas.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="p-reservation">Monto de separación</Label>
                <div className="flex gap-2">
                  <Input
                    id="p-reservation"
                    type="number"
                    min={0}
                    step="0.01"
                    value={f.reservationAmount}
                    onChange={(e) => set({ reservationAmount: e.target.value })}
                    placeholder="1000"
                  />
                  <Select
                    aria-label="Moneda de la separación"
                    value={f.reservationCurrency}
                    onChange={(e) => set({ reservationCurrency: e.target.value as 'USD' | 'PEN' })}
                    className="w-24"
                  >
                    <option value="USD">US$</option>
                    <option value="PEN">S/</option>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="p-initial">Cuota inicial (%)</Label>
                <div className="flex gap-2">
                  <Input
                    id="p-initial"
                    type="number"
                    min={0}
                    max={100}
                    step="0.5"
                    value={f.initialFeePercent}
                    onChange={(e) => set({ initialFeePercent: e.target.value })}
                    placeholder="10"
                  />
                  <Input
                    aria-label="Cuotas de la inicial"
                    type="number"
                    min={1}
                    max={60}
                    value={f.initialInstallments}
                    onChange={(e) => set({ initialInstallments: e.target.value })}
                    placeholder="cuotas"
                    className="w-24"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Formas de pago</Label>
                <div className="flex flex-wrap gap-2">
                  {Object.values(FinancingType).map((t) => (
                    <label
                      key={t}
                      className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${f.financingTypes.includes(t) ? 'border-primary bg-primary/10' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={f.financingTypes.includes(t)}
                        onChange={() => set({ financingTypes: toggleIn(f.financingTypes, t) })}
                        className="h-3 w-3"
                      />
                      {FINANCING_LABELS[t] ?? t}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <Label>Programas y bonos</Label>
                <div className="flex flex-wrap gap-2">
                  {PROJECT_PROGRAMS.map((pr) => (
                    <label
                      key={pr}
                      className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${f.programs.includes(pr) ? 'border-primary bg-primary/10' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={f.programs.includes(pr)}
                        onChange={() => set({ programs: toggleIn(f.programs, pr) })}
                        className="h-3 w-3"
                      />
                      {PROJECT_PROGRAM_LABELS[pr]}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="p-banks">Bancos con los que se trabaja (separados por coma)</Label>
                <Input
                  id="p-banks"
                  value={f.banks}
                  onChange={(e) => set({ banks: e.target.value })}
                  placeholder="BCP, BBVA, Interbank"
                />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="p-promotions">Promociones vigentes</Label>
                <Textarea
                  id="p-promotions"
                  rows={2}
                  value={f.promotions}
                  onChange={(e) => set({ promotions: e.target.value })}
                  placeholder="Bono de US$ 2,000 en separaciones de setiembre, estacionamiento incluido…"
                  maxLength={1000}
                />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="p-notes">Notas para el equipo y el bot</Label>
                <Textarea
                  id="p-notes"
                  rows={3}
                  value={f.notes}
                  onChange={(e) => set({ notes: e.target.value })}
                  placeholder="Sala de ventas de lunes a domingo 10–18 h, entrega con acabados, no se aceptan mascotas grandes…"
                  maxLength={2000}
                />
              </div>
            </div>
          </div>
          <div className="space-y-2 border-t pt-3" data-testid="project-sales-room">
            <div>
              <p className="text-sm font-medium">Horario de la sala de ventas</p>
              <p className="text-muted-foreground text-xs">
                El bot y la agenda ofrecen citas solo dentro de este horario. Si no se marca, se usa
                el horario de atención de la empresa.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={f.roomCustom}
                onChange={(e) => set({ roomCustom: e.target.checked })}
              />
              Tiene su propio horario
            </label>
            {f.roomCustom && (
              <div className="grid gap-2 sm:grid-cols-[1fr_1fr_2fr]">
                <div className="space-y-1">
                  <Label htmlFor="p-room-start">Abre</Label>
                  <Input
                    id="p-room-start"
                    type="time"
                    value={f.roomStart}
                    onChange={(e) => set({ roomStart: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="p-room-end">Cierra</Label>
                  <Input
                    id="p-room-end"
                    type="time"
                    value={f.roomEnd}
                    onChange={(e) => set({ roomEnd: e.target.value })}
                  />
                </div>
                <div className="flex flex-wrap items-end gap-2 text-xs">
                  {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                    <label key={d} className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5"
                        checked={f.roomDays.includes(d)}
                        onChange={(e) =>
                          set({
                            roomDays: e.target.checked
                              ? [...f.roomDays, d].sort()
                              : f.roomDays.filter((x) => x !== d),
                          })
                        }
                      />
                      {DAY_SHORT[d]}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="space-y-3 border-t pt-3" data-testid="project-bot">
            <div>
              <p className="text-sm font-medium">Lo que sabe el bot</p>
              <p className="text-muted-foreground text-xs">
                La ficha se arma sola con los datos del proyecto y el stock (precios «desde» por
                tipología). Aquí agregas preguntas frecuentes e indicaciones propias.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="p-bot-instructions">Indicaciones para el bot</Label>
              <Textarea
                id="p-bot-instructions"
                rows={2}
                value={f.botInstructions}
                onChange={(e) => set({ botInstructions: e.target.value })}
                placeholder="Ej.: destacar la terraza común; no ofrecer estacionamientos, están agotados"
                maxLength={2000}
              />
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium">Preguntas frecuentes</p>
              {f.botFaqs.map((faq, i) => (
                <div
                  key={i}
                  className="grid gap-1 sm:grid-cols-[1fr_1fr_auto]"
                  data-testid="project-faq"
                >
                  <Input
                    value={faq.q}
                    onChange={(e) =>
                      set({
                        botFaqs: f.botFaqs.map((x, j) =>
                          j === i ? { ...x, q: e.target.value } : x,
                        ),
                      })
                    }
                    placeholder="Pregunta"
                    aria-label={`Pregunta ${i + 1}`}
                    maxLength={200}
                  />
                  <Input
                    value={faq.a}
                    onChange={(e) =>
                      set({
                        botFaqs: f.botFaqs.map((x, j) =>
                          j === i ? { ...x, a: e.target.value } : x,
                        ),
                      })
                    }
                    placeholder="Respuesta"
                    aria-label={`Respuesta ${i + 1}`}
                    maxLength={600}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={`Quitar pregunta ${i + 1}`}
                    onClick={() => set({ botFaqs: f.botFaqs.filter((_, j) => j !== i) })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {f.botFaqs.length < 20 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => set({ botFaqs: [...f.botFaqs, { q: '', a: '' }] })}
                  data-testid="btn-add-faq"
                >
                  <Plus className="h-4 w-4" /> Agregar pregunta
                </Button>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="p-bot-prices">Precios por este proyecto</Label>
                <Select
                  id="p-bot-prices"
                  value={f.botPriceMode}
                  onChange={(e) => set({ botPriceMode: e.target.value as '' | 'DESDE' | 'NO' })}
                >
                  <option value="">Como la empresa</option>
                  <option value="DESDE">Da precios «desde»</option>
                  <option value="NO">No da precios</option>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium">
                Precios «desde» a mano (reemplazan a los del stock)
              </p>
              {f.botPrices.map((row, i) => (
                <div key={i} className="grid gap-1 sm:grid-cols-[1fr_1fr_auto_auto]">
                  <Input
                    value={row.typology}
                    onChange={(e) =>
                      set({
                        botPrices: f.botPrices.map((x, j) =>
                          j === i ? { ...x, typology: e.target.value } : x,
                        ),
                      })
                    }
                    placeholder="Tipología (ej. 2D)"
                    aria-label={`Tipología ${i + 1}`}
                  />
                  <Input
                    type="number"
                    min={0}
                    value={row.from}
                    onChange={(e) =>
                      set({
                        botPrices: f.botPrices.map((x, j) =>
                          j === i ? { ...x, from: e.target.value } : x,
                        ),
                      })
                    }
                    placeholder="Desde"
                    aria-label={`Precio desde ${i + 1}`}
                  />
                  <Select
                    value={row.currency}
                    onChange={(e) =>
                      set({
                        botPrices: f.botPrices.map((x, j) =>
                          j === i ? { ...x, currency: e.target.value as 'USD' | 'PEN' } : x,
                        ),
                      })
                    }
                    aria-label={`Moneda ${i + 1}`}
                    className="w-24"
                  >
                    <option value="USD">US$</option>
                    <option value="PEN">S/</option>
                  </Select>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={`Quitar precio ${i + 1}`}
                    onClick={() => set({ botPrices: f.botPrices.filter((_, j) => j !== i) })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {f.botPrices.length < 20 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    set({
                      botPrices: [...f.botPrices, { typology: '', from: '', currency: 'USD' }],
                    })
                  }
                >
                  <Plus className="h-4 w-4" /> Agregar precio
                </Button>
              )}
            </div>
            {project && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPreview(true)}
                data-testid="btn-bot-preview"
              >
                Ver lo que sabe el bot hoy
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={create.isPending || update.isPending}
              data-testid="project-save"
            >
              Guardar
            </Button>
          </DialogFooter>
        </form>
        {project && preview && (
          <KnowledgePreview projectId={project.id} onClose={() => setPreview(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SubdivisionDialog({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (i: { name: string; code: string; sortOrder: number }) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void onSave({ name, code: code || name.toUpperCase().slice(0, 10), sortOrder: 0 });
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>Nueva torre / etapa</DialogTitle>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="s-name">Nombre</Label>
            <Input
              id="s-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              placeholder="Torre B"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="s-code">Código</Label>
            <Input
              id="s-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="B"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit">Crear</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Texto exacto que ve el modelo (ficha de conocimiento del proyecto). */
function KnowledgePreview({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const { data, isLoading } = useBotKnowledge(projectId);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Lo que sabe el bot</DialogTitle>
        </DialogHeader>
        {isLoading || !data ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <pre
            className="bg-muted max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-md p-3 text-xs"
            data-testid="bot-knowledge-text"
          >
            {data.text}
          </pre>
        )}
      </DialogContent>
    </Dialog>
  );
}
